// src/services/whsInboxService.ts
// ─────────────────────────────────────────────────────────────────
// WHS Inbox Service — Phase 4 (Mock)
//
// Phase 4 additions over Phase 1:
//   • createPlatformWHSRecord() — bi-directional sync: submission
//     → platform WHS module record (4C-02)
//   • getSubmissionWithRecord()  — reads submission + linked record
//   • updateSubmissionStatus()   — now also updates linked WHS record
//   • fireCriticalAlert()        — now also creates DRAFT WHSO alert
//
// At beta: replace function bodies with fetch('/api/whs-inbox/...')
// ─────────────────────────────────────────────────────────────────

import { MOCK_WHS_NOTIFICATIONS, type WHSNotification } from '../utils/mockDataMobile'
import type {
  WHSSubmission, WHSSubmissionType, WHSSubmissionStatus,
} from '../types/mobileCrewTypes'
import { addCrewNotification }   from '../utils/mockDataNotificationBus'
import type { IncidentAlertPayload } from '../types/mobileCrewTypes'
import {
  addWHSRecord, getWHSRecordBySubmissionId, updateWHSRecordStatus,
  type WHSRecord, type WHSRecordModule,
} from '../utils/mockDataWHSRecords'
import { createAlert }           from './whsoAlertService'

// ── Mapping helpers ────────────────────────────────────────────────
function toSubmissionType(type: string, severity: string): WHSSubmissionType {
  if (type === 'INCIDENT')  return severity === 'CRITICAL' ? 'INCIDENT' : 'NEAR_MISS'
  if (type === 'BREACH')    return 'UNSAFE_CONDITION'
  if (type === 'CONCERN')   return 'HAZARD_IDENTIFICATION'
  return 'INCIDENT'
}

function toSubmissionStatus(whs: string): WHSSubmissionStatus {
  const map: Record<string, WHSSubmissionStatus> = {
    SUBMITTED:'RECEIVED', UNDER_REVIEW:'UNDER_REVIEW',
    ACTION_TAKEN:'PENDING_CLOSE_OUT', CLOSED:'CLOSED',
  }
  return map[whs] ?? 'RECEIVED'
}

// Map WHSSubmissionType to platform WHS module and record type
const SUBMISSION_MODULE_MAP: Record<WHSSubmissionType, { module: WHSRecordModule; title: string }> = {
  INCIDENT:             { module:'incidents',  title:'Incident report' },
  NEAR_MISS:            { module:'incidents',  title:'Near-miss report' },
  HAZARD_IDENTIFICATION:{ module:'inspections',title:'Hazard / corrective action' },
  UNSAFE_CONDITION:     { module:'inspections',title:'Unsafe condition / corrective action' },
  SWMS_NON_COMPLIANCE:  { module:'swms-flags', title:'SWMS non-compliance flag' },
}

// ── Session overrides (status changes, platform links) ─────────────
let _statusOverrides: Record<string, WHSSubmissionStatus> = {
  // Pre-seed: whs001 and whs003 are already linked to platform records
  whs001: 'UNDER_REVIEW',
  whs002: 'PENDING_CLOSE_OUT',
  whs003: 'UNDER_REVIEW',
}
let _platformLinks: Record<string, { recordId: string; module: string; reference: string }> = {
  whs001: { recordId:'whsr_001', module:'WHS → Incidents',   reference:'INC-2026-0038' },
  whs002: { recordId:'whsr_002', module:'WHS → Inspections', reference:'INSP-CA-0091'  },
  whs003: { recordId:'whsr_003', module:'WHS → Incidents',   reference:'INC-2026-0039' },
}

function toWHSSubmission(n: WHSNotification): WHSSubmission {
  return {
    id:                  n.id,
    reference:           n.reference,
    type:                toSubmissionType(n.type, n.severity),
    submittedById:       n.submittedById,
    submittedByName:     n.submittedByName,
    submittedByRole:     n.submittedByRole,
    submittedAt:         n.submittedAt,
    jobId:               n.jobId,
    jobNumber:           n.jobNumber,
    location:            n.gpsAddress ?? `${n.jobNumber} site`,
    gpsLat:              n.gpsLat,
    gpsLng:              n.gpsLng,
    severity:            n.severity,
    description:         n.description,
    photoCount:          n.photoCount,
    status:              _statusOverrides[n.id]        ?? toSubmissionStatus(n.status),
    platformWHSRecordId: _platformLinks[n.id]?.recordId ?? null,
    platformWHSModule:   _platformLinks[n.id]?.module   ?? null,
    reviewedById:        null,
    reviewedByName:      null,
    reviewedAt:          null,
    pmNotes:             n.pmNotes,
    actionsTaken:        n.actionsTaken,
    closedById:          n.closedById,
    closedByName:        n.closedByName,
    closedAt:            n.closedAt,
    regulatorNotified:   n.worksafeNotified,
    regulatorRef:        n.worksafeRef,
    submittedOffline:    n.submittedOffline,
    offlineDelayMinutes: n.offlineSubmissionDelay,
  }
}

// ── Exported service functions ─────────────────────────────────────

export interface GetSubmissionsFilter {
  jobId?:    string
  severity?: string
  status?:   WHSSubmissionStatus
  type?:     WHSSubmissionType
  linked?:   boolean   // true = only submissions with a platform record
}

/** All WHS inbox submissions with current status and platform links. */
export async function getWHSSubmissions(filter?: GetSubmissionsFilter): Promise<WHSSubmission[]> {
  // Phase 6: GET /api/whs-inbox?...
  let results = MOCK_WHS_NOTIFICATIONS.map(toWHSSubmission)
  if (filter?.jobId)    results = results.filter(s => s.jobId === filter.jobId)
  if (filter?.severity) results = results.filter(s => s.severity === filter.severity)
  if (filter?.status)   results = results.filter(s => s.status === filter.status)
  if (filter?.type)     results = results.filter(s => s.type === filter.type)
  if (filter?.linked !== undefined) {
    results = results.filter(s => filter.linked ? !!s.platformWHSRecordId : !s.platformWHSRecordId)
  }
  return Promise.resolve(results)
}

/** Single submission with its linked platform record. */
export async function getSubmissionWithRecord(submissionId: string): Promise<{
  submission: WHSSubmission | null
  record:     WHSRecord | null
}> {
  // Phase 6: GET /api/whs-inbox/:id?include=whs-record
  const n = MOCK_WHS_NOTIFICATIONS.find(x => x.id === submissionId)
  if (!n) return { submission: null, record: null }
  const submission = toWHSSubmission(n)
  const record     = submission.platformWHSRecordId
    ? getWHSRecordBySubmissionId(submissionId)
    : null
  return Promise.resolve({ submission, record })
}

/**
 * 4C-02 — Bi-directional sync: create a corresponding platform WHS record
 * from a mobile submission. The PM does NOT re-enter data manually.
 * At beta: POST /api/whs-inbox/:id/create-whs-record
 */
export async function createPlatformWHSRecord(
  submissionId: string,
  createdById: string,
  createdByName: string,
): Promise<WHSRecord | null> {
  const n = MOCK_WHS_NOTIFICATIONS.find(x => x.id === submissionId)
  if (!n) return null
  if (_platformLinks[submissionId]) return getWHSRecordBySubmissionId(submissionId)

  const subType = toSubmissionType(n.type, n.severity)
  const modMeta = SUBMISSION_MODULE_MAP[subType]

  const record = addWHSRecord({
    module:        modMeta.module,
    sourceType:    'MOBILE_SUBMISSION',
    submissionId,
    jobId:         n.jobId,
    jobNumber:     n.jobNumber,
    title:         `${modMeta.title} — ${n.reference}`,
    description:   n.description,
    severity:      n.severity,
    status:        'OPEN',
    createdById,
    createdByName,
    assignedToId:  null,
    closedAt:      null,
  })

  // Link the submission back to the new platform record
  _platformLinks[submissionId] = {
    recordId:  record.id,
    module:    `WHS → ${modMeta.module === 'incidents' ? 'Incidents' : modMeta.module === 'inspections' ? 'Inspections' : 'SWMS'}`,
    reference: record.reference,
  }

  return Promise.resolve(record)
}

/** Update submission status — also syncs to linked platform record. */
export async function updateSubmissionStatus(
  submissionId: string,
  newStatus:    WHSSubmissionStatus,
  reviewedById: string,
  reviewedByName: string,
): Promise<boolean> {
  // Phase 6: PATCH /api/whs-inbox/:id/status
  _statusOverrides[submissionId] = newStatus

  // Sync status to the linked platform WHS record
  const link = _platformLinks[submissionId]
  if (link) {
    const whsStatus = newStatus === 'RECEIVED'          ? 'OPEN' :
                      newStatus === 'UNDER_REVIEW'      ? 'UNDER_INVESTIGATION' :
                      newStatus === 'PENDING_CLOSE_OUT' ? 'CORRECTIVE_ACTION' :
                      'CLOSED'
    updateWHSRecordStatus(link.recordId, whsStatus as any)
  }

  return Promise.resolve(true)
}

/** Link a submission to an existing platform WHS record. */
export async function linkToWHSModule(
  submissionId:   string,
  platformRecordId: string,
  platformModule: string,
  reference:      string,
): Promise<boolean> {
  // Phase 6: PATCH /api/whs-inbox/:id/link
  _platformLinks[submissionId] = { recordId: platformRecordId, module: platformModule, reference }
  return Promise.resolve(true)
}

/**
 * 4C-05 — Auto-fire CRITICAL/HIGH submission alert to PM + WHSO.
 * Now also creates a DRAFT WHSO alert for the WHS Alerts sub-tab.
 * Called by WHS Inbox component when a CRITICAL/HIGH submission arrives.
 */
export async function fireCriticalAlert(
  submission:     WHSSubmission,
  pmEmployeeId:   string,
  whsoEmployeeId?: string,
): Promise<void> {
  if (submission.severity !== 'CRITICAL' && submission.severity !== 'HIGH') return

  const recipientIds = [pmEmployeeId, ...(whsoEmployeeId ? [whsoEmployeeId] : [])]

  addCrewNotification({
    type:          'INCIDENT_ALERT',
    priority:      submission.severity === 'CRITICAL' ? 'CRITICAL' : 'HIGH',
    employeeIds:   recipientIds,
    jobIds:        [submission.jobId],
    heading:       `${submission.severity} WHS submission — ${submission.jobNumber} — ${submission.reference}`,
    body:          `${submission.severity} ${submission.type.replace(/_/g,' ')} submitted by ${submission.submittedByName}: ${submission.description.slice(0,200)}`,
    requiredAction:submission.severity === 'CRITICAL'
      ? 'Review immediately. Consider site recall. Contact WHSO officer. WorkSafe QLD notification may be required.'
      : 'Review within 1 hour. Assign corrective action.',
    sourceModule:  'whsInboxService',
    sourceRecordId:submission.id,
    requiresAck:   true,
    payload: {
      incidentRef:         submission.reference,
      incidentType:        submission.type,
      severity:            submission.severity,
      location:            submission.location,
      initialActions:      'Under review by PM',
      investigationStatus: 'RECEIVED',
    } as IncidentAlertPayload,
  })

  // Create a DRAFT WHSO alert for PM to review and send to crew
  await createAlert({
    alertType:               submission.severity === 'CRITICAL' ? 'EMERGENCY' : 'WHS_HIGH_RISK',
    severity:                submission.severity as any,
    affectedJobIds:          [submission.jobId],
    affectedJobNumbers:      [submission.jobNumber],
    affectedCrewIds:         [],
    heading:                 `${submission.severity} WHS report received — ${submission.reference}`,
    body:                    `A ${submission.severity} ${submission.type.replace(/_/g,' ')} has been reported by ${submission.submittedByName}: ${submission.description.slice(0,200)}`,
    requiredAction:          submission.severity === 'CRITICAL'
      ? 'Do not enter affected area until PM gives clearance.'
      : 'Await PM instructions. Report any related hazards immediately.',
    acknowledgementRequired: true,
    linkedWHSRecordId:       submission.id,
    linkedWHSModule:         'WHS → WHS Inbox',
    source:                  'AUTO_INCIDENT',
    createdById:             'system',
    createdByName:           `Auto-alert (${submission.type.replace(/_/g,' ')})`,
  })
}

/** Convenience helper — is this submission linked to a platform record? */
export function isLinkedToWHSModule(submissionId: string): boolean {
  return !!_platformLinks[submissionId]
}

/** Get the platform record reference for display (e.g. 'INC-2026-0038'). */
export function getLinkedReference(submissionId: string): string | null {
  return _platformLinks[submissionId]?.reference ?? null
}
